// src/components/SurgeryPanel.tsx (v2.5)
// 手術タブ: 手術件数の月次推移と診療科別ランキング (+ 予算比較)
//
// チャート:
//   ① 手術件数 月次トレンド (病院全体): 実績 + 予算
//   ② 予定 / 緊急 構成比 (積み上げ棒、月次)
//   ③ 診療科別 手術件数 ランキング (期間累計)
//   ④ 診療科別 手術件数 月次 heatmap
//
// 注: 「手術売上」は別データソース (kb_all.parquet の手術小計集計) が必要なため将来課題。
//     簡易推定は誤解を生むので、ここでは件数のみ表示。
import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import * as Plot from "@observablehq/plot";
import { api } from "../lib/api";
import { useFilters, monthShift } from "../store";
import { PlotFigure } from "./Plot";
import { CsvButton } from "./CsvButton";
import { buildTimeAxis, buildMonthAxisMark } from "../lib/chartAxis";
import { useSort, SortTh } from "../lib/useSort";
import { ymJp } from "../lib/format";
import type { SurgeryResponse } from "@shared/api";

// 診療科別 手術件数 集計表の行 (deptTable.rows 要素・SortTh の get キャスト用)
type DeptTableRow = {
  診療科: string;
  cells: Map<string, { actual: number | null; prev: number | null }>;
  actualMean: number | null;
  prevMean: number | null;
  pctMean: number | null;
  ratioMean: number | null;
};

// null 無視の平均 (直近12ヶ月移動平均用)
const mean = (xs: (number | null | undefined)[]): number | null => {
  const v = xs.filter((x): x is number => x != null);
  return v.length ? v.reduce((s, x) => s + x, 0) / v.length : null;
};

export function SurgeryPanel() {
  const f = useFilters();
  const focusDept = f.surgeryFocusedDept;
  const scopeLabel = focusDept ?? "病院全体";

  // v2.6b: 大分類 (診療科) ⇄ 小分類 (外科→消化器/乳腺内分泌/呼吸器, 202404+) トグル。
  // store 管理 (サイドバーの診療科リストを同粒度に追従させるため)。
  const dim = f.surgeryDim;

  // 表示形式「直近12ヶ月平均」: 各表示月で末尾12ヶ月の移動平均を出すため 12ヶ月余分に取得 (2026-06-18)。
  // 行為別タブ (KbRevenuePanel) と同じ挙動 — 表/グラフの各月セルを移動平均に切替える。
  const avgMode = f.periodPreset === "直近12ヶ月平均";
  const fetchFrom = avgMode ? monthShift(f.periodFrom, -12) : f.periodFrom;

  const { data, isLoading } = useQuery<SurgeryResponse>({
    queryKey: ["surgery", fetchFrom, f.periodTo, dim],
    queryFn: () => api.surgery({ periodFrom: fetchFrom, periodTo: f.periodTo, dim }),
    placeholderData: (prev) => prev,
  });

  // focus 適用後の rows (focus null なら全科)
  const scopedRows = useMemo(() => {
    if (!data) return [];
    if (!focusDept) return data.rows;
    return data.rows.filter((r) => r["診療科"] === focusDept);
  }, [data, focusDept]);


  // 月次合算 (focus null=病院全体 / focus 設定時=単科の月次)。
  // avgMode は表示窓の前に12ヶ月余分に取得しているので、 全取得月を metric ごとに集計し、
  // 表示は dispMonths (= 要求窓 periodFrom〜periodTo) のみ・各月は seriesVal で移動平均/単月を切替。
  const { byMetric, allMonths } = useMemo(() => {
    const byMetric = new Map<string, Map<string, { actual: number; budget: number; bcount: number; py: number; pcount: number }>>();
    const ms = new Set<string>();
    for (const r of scopedRows) {
      ms.add(r.date);
      if (!byMetric.has(r.集計区分)) byMetric.set(r.集計区分, new Map());
      const x = byMetric.get(r.集計区分)!;
      if (!x.has(r.date)) x.set(r.date, { actual: 0, budget: 0, bcount: 0, py: 0, pcount: 0 });
      const entry = x.get(r.date)!;
      entry.actual += Number(r.実績);
      if (r.予算 != null)     { entry.budget += Number(r.予算); entry.bcount++; }
      if (r.前年実績 != null) { entry.py     += Number(r.前年実績); entry.pcount++; }
    }
    return { byMetric, allMonths: [...ms].sort() };
  }, [scopedRows]);

  // 表示月 = 要求窓 (periodFrom〜periodTo)。 avgMode でも表示窓は同じ、 値だけ移動平均。
  const dispMonths = useMemo(
    () => allMonths.filter((d) => d >= f.periodFrom && d <= f.periodTo),
    [allMonths, f.periodFrom, f.periodTo]
  );

  // metric/date の値: avgMode は「その月までの末尾12ヶ月移動平均」、 そうでなければ単月。
  const seriesVal = useMemo(() => {
    return (metric: string, date: string, field: "actual" | "budget" | "py"): number | null => {
      const m = byMetric.get(metric);
      if (!m) return null;
      const one = (d: string): number | null => {
        const e = m.get(d);
        if (!e) return null;
        if (field === "actual") return e.actual;
        if (field === "budget") return e.bcount > 0 ? e.budget : null;
        return e.pcount > 0 ? e.py : null;
      };
      if (!avgMode) return one(date);
      const i = allMonths.indexOf(date);
      return mean(allMonths.slice(Math.max(0, i - 11), i + 1).map(one));
    };
  }, [byMetric, allMonths, avgMode]);

  // 件数の表示書式: avgMode は移動平均なので小数1桁、 単月は整数。
  const fmtCnt = (v: number | null | undefined): string =>
    v == null ? "—" : avgMode ? v.toFixed(1) : Math.round(v).toLocaleString();

  // 期間累計 (診療科別) — focus 時は当該科 1 行のみ

  // 件数 vs 予算 (月次、 avgMode=移動平均)。 表示窓 dispMonths のみ。
  const kensuLine = useMemo(() => dispMonths.map((d) => ({
    date: new Date(d),
    value: seriesVal("手術件数", d, "actual") ?? 0,
    budget: seriesVal("手術件数", d, "budget") ?? undefined,
    prevYear: seriesVal("手術件数", d, "py") ?? undefined,
  })), [dispMonths, seriesVal]);

  // 月次推移共通 x domain (毎月 tick を強制するため明示)
  // Plot v0.6 auto-thinning が 12 ヶ月を 11 tick に間引く問題への回避策 (2026-05-21)
  const xDomain = useMemo<Date[] | undefined>(() => {
    if (kensuLine.length === 0) return undefined;
    return [kensuLine[0].date, kensuLine[kensuLine.length - 1].date];
  }, [kensuLine]);

  // ① 月次トレンド (病院全体): 実績 + 予算 + 前年同月
  const kensuOpts = useMemo<Plot.PlotOptions>(() => ({
    width: 1100, height: 280, marginLeft: 60, marginRight: 60, marginTop: 16, marginBottom: 32,
    style: { fontFamily: "var(--jp-font, system-ui)", fontSize: "11px" },
    x: buildTimeAxis({ domain: xDomain }),  // axis: null + 下の buildMonthAxisMark で毎月 tick 強制
    y: { label: "手術件数 (件)", grid: true, nice: true, zero: false },
    color: { domain: ["実績", "予算", "前年同月"], range: ["#1B9E77", "#d97706", "#94a3b8"], legend: true },
    marks: [
      buildMonthAxisMark(xDomain),
      // 前年同月: 薄灰 line + dot (TrendGrid と同形式、 旧 areaY 廃止で y 軸自動調整)
      Plot.lineY(kensuLine.filter((d) => d.prevYear != null), {
        x: "date", y: "prevYear", stroke: "#94a3b8", strokeWidth: 1.5, strokeOpacity: 0.6,
      }),
      Plot.dot(kensuLine.filter((d) => d.prevYear != null), {
        x: "date", y: "prevYear", fill: "#94a3b8", fillOpacity: 0.6, r: 1.8,
      }),
      // 予算: 強調点線 (橙、 TrendGrid と同 #d97706 / 1.8 / "5 3")
      Plot.lineY(kensuLine.filter((d) => d.budget != null), {
        x: "date", y: "budget", stroke: "#d97706", strokeWidth: 1.8, strokeDasharray: "5 3",
      }),
      Plot.dot(kensuLine.filter((d) => d.budget != null), {
        x: "date", y: "budget", fill: "#d97706", r: 1.8,
      }),
      // 実績: 実線 (予算より太く、 最後に描画して上位レイヤー)
      Plot.lineY(kensuLine, { x: "date", y: "value", stroke: "#1B9E77", strokeWidth: 2.4 }),
      Plot.dot(kensuLine, { x: "date", y: "value", fill: "#1B9E77", r: 2.4 }),
      Plot.tip(kensuLine, Plot.pointerX({
        x: "date", y: "value",
        title: (d) => `${ymJp(d.date.toISOString())}${avgMode ? " (直近12ヶ月移動平均)" : ""}\n実績: ${fmtCnt(d.value)} 件`
          + (d.budget != null ? `\n予算: ${fmtCnt(d.budget)} 件` : "")
          + (d.prevYear != null ? `\n前年同月: ${fmtCnt(d.prevYear)} 件` : ""),
      })),
    ].filter(Boolean) as Plot.Markish[],
  }), [kensuLine, xDomain, avgMode]);

  // ② 予定 / 緊急 構成 (積み上げ、各 bar に件数ラベル)。 avgMode=移動平均。
  const yoteiData = useMemo(() => {
    const out: { ym: string; metric: string; value: number }[] = [];
    for (const metric of ["予定", "緊急"]) {
      for (const d of dispMonths) {
        out.push({ ym: d.slice(0, 7), metric, value: seriesVal(metric, d, "actual") ?? 0 });
      }
    }
    return out;
  }, [dispMonths, seriesVal]);
  // 月合計 (棒の上に表示)
  const yoteiTotalByMonth = useMemo(() => {
    const m = new Map<string, number>();
    yoteiData.forEach((r) => m.set(r.ym, (m.get(r.ym) ?? 0) + r.value));
    return [...m.entries()].map(([ym, total]) => ({ ym, total }));
  }, [yoteiData]);

  const yoteiOpts = useMemo<Plot.PlotOptions>(() => ({
    width: 1100, height: 280, marginLeft: 60, marginRight: 60, marginTop: 24, marginBottom: 32,
    style: { fontFamily: "var(--jp-font, system-ui)", fontSize: "11px" },
    x: { label: null, type: "band", domain: [...new Set(yoteiData.map(r => r.ym))].sort() },
    y: { label: "件数", grid: true },
    color: { domain: ["予定", "緊急"], range: ["#1B9E77", "#dc2626"], legend: true },
    marks: [
      Plot.barY(yoteiData, { x: "ym", y: "value", fill: "metric" }),
      // 各 segment 中央 件数ラベル (白文字)
      Plot.text(yoteiData, Plot.stackY({
        x: "ym", y: "value",
        text: (d) => fmtCnt(d.value),
        fontSize: 10, fontWeight: "bold", fill: "white",
      })),
      // 棒の上に月合計ラベル
      Plot.text(yoteiTotalByMonth, {
        x: "ym", y: "total",
        text: (d) => fmtCnt(d.total),
        textAnchor: "middle", dy: -6,
        fontSize: 11, fontWeight: "bold", fill: "#1f2937",
      }),
      Plot.tip(yoteiData, Plot.pointer({
        x: "ym", y: "value",
        title: (d) => `${d.ym} ${d.metric}: ${fmtCnt(d.value)} 件`,
      })),
    ],
  }), [yoteiData, yoteiTotalByMonth, avgMode]);

  // ④ 診療科別 月次集計表 (実績 / 前年同月 / 月平均 / 対前年)
  //   病院全体 (= 総合計) を最上行に、 他科は 月平均 降順
  const deptTable = useMemo(() => {
    type Cell = { actual: number | null; prev: number | null };
    type Row = { 診療科: string; cells: Map<string, Cell>; actualMean: number | null; prevMean: number | null; pctMean: number | null; ratioMean: number | null };
    // 全取得月の単月セル (avgMode の移動平均算出に窓外12ヶ月も必要)
    const rawByDept = new Map<string, Map<string, Cell>>();
    let rawTotal: Map<string, Cell> | null = null;
    for (const r of data?.rows ?? []) {
      if (r.集計区分 !== "手術件数") continue;
      const isTotal = r["診療科"] === "総合計";
      const cells = isTotal
        ? (rawTotal ?? (rawTotal = new Map<string, Cell>()))
        : (rawByDept.get(r["診療科"]) ?? (() => { const x = new Map<string, Cell>(); rawByDept.set(r["診療科"], x); return x; })());
      cells.set(r.date, {
        actual: r.実績 != null ? Number(r.実績) : null,
        prev:   r.前年実績 != null ? Number(r.前年実績) : null,
      });
    }
    const months = dispMonths;
    // 表示セル値: avgMode は末尾12ヶ月移動平均、 そうでなければ単月
    const cellVal = (cells: Map<string, Cell>, mo: string, key: "actual" | "prev"): number | null => {
      if (!avgMode) return cells.get(mo)?.[key] ?? null;
      const i = allMonths.indexOf(mo);
      return mean(allMonths.slice(Math.max(0, i - 11), i + 1).map((m) => cells.get(m)?.[key] ?? null));
    };
    const toRow = (name: string, cells: Map<string, Cell>): Row => {
      const disp = new Map<string, Cell>();
      // 月平均/前年/対前年 は「単月実績」の窓内平均 (= トグルで変わらない真の月平均)
      let sa = 0, ca = 0, sp = 0, cp = 0;
      for (const mo of months) {
        disp.set(mo, { actual: cellVal(cells, mo, "actual"), prev: cellVal(cells, mo, "prev") });
        const ra = cells.get(mo)?.actual;
        const rp = cells.get(mo)?.prev;
        if (ra != null) { sa += ra; ca++; }
        if (rp != null) { sp += rp; cp++; }
      }
      const actualMean = ca > 0 ? sa / ca : null;
      const prevMean   = cp > 0 ? sp / cp : null;
      const pctMean    = (actualMean != null && prevMean != null && prevMean !== 0)
                          ? (actualMean - prevMean) / Math.abs(prevMean) : null;
      const ratioMean  = (actualMean != null && prevMean != null && prevMean !== 0)
                          ? actualMean / prevMean : null;
      return { 診療科: name, cells: disp, actualMean, prevMean, pctMean, ratioMean };
    };
    const deptRows = [...rawByDept].map(([d, c]) => toRow(d, c));
    deptRows.sort((a, b) => (b.actualMean ?? -Infinity) - (a.actualMean ?? -Infinity));
    const rows: Row[] = [];
    if (rawTotal) rows.push(toRow("病院全体", rawTotal));
    rows.push(...deptRows);
    return { rows, months };
  }, [data, dispMonths, allMonths, avgMode]);

  // セル色: ±5%/±10% 5 階調 (手術件数は higher_is_better)
  const cellBg = (pct: number | null): string => {
    if (pct == null) return "";
    if (pct >= 0.10) return "bg-emerald-300";
    if (pct >= 0.05) return "bg-emerald-100";
    if (pct >= -0.05) return "";
    if (pct >= -0.10) return "bg-red-100";
    return "bg-red-300";
  };
  // 月ラベル: 期間起点 (= 集計表最初の月) と 1 月 (= 年跨ぎ) に年付き
  // 例: 期間 2025-05 〜 2026-04 → 「25/5月, 6月, 7月, ..., 26/1月, 2月, 3月, 4月」
  const firstMonth = deptTable.months[0];
  const monthLabel = (s: string) => {
    const [y, mo] = s.split("-");
    const mi = parseInt(mo, 10);
    return s === firstMonth || mi === 1 ? `${y.slice(2)}/${mi}月` : `${mi}月`;
  };

  // 集計表 CSV
  const deptTableCsv = useMemo(() => deptTable.rows.map((r) => {
    const row: Record<string, string | number> = {
      診療科: r.診療科,
      月平均: r.actualMean ?? "",
      "月平均(前年)": r.prevMean ?? "",
      "対前年%": r.ratioMean != null ? Number((r.ratioMean * 100).toFixed(1)) : "",
    };
    deptTable.months.forEach((mo) => { row[mo] = r.cells.get(mo)?.actual ?? ""; });
    return row;
  }), [deptTable]);

  // クリックソート: 「病院全体」(総合計) は先頭固定、他科を並べ替え。key 未設定時は既定順(月平均降順)。
  const s = useSort(deptTable.rows, { pin: (r) => r.診療科 === "病院全体" });

  // 集計粒度トグル (大分類⇄小分類) は サイドバー (FilterBar) へ移設 (2026-06-17)。
  // dim は f.surgeryDim を参照 (クエリ・表示に使用)。
  return (
    <div className="space-y-3">
      <section className="card p-4">
        <div className="flex items-center gap-2 mb-2">
          <h2 className="text-base font-semibold">📅 手術件数 {avgMode ? "直近12ヶ月移動平均" : "月次推移"} ({scopeLabel})</h2>
          {avgMode && <span className="perf-pill bg-amber-100 text-amber-800 border-amber-200">各月=末尾12ヶ月移動平均</span>}
          {data && <span className="perf-pill">SQL {data.sqlMs} ms / {data.rows.length} 行</span>}
          {isLoading && <span className="text-xs text-slate-400">loading…</span>}
          <CsvButton
            rows={kensuLine.map((r) => ({ date: r.date.toISOString().slice(0,10), 手術件数: r.value, 予算: r.budget ?? null }))}
            filename={`surgery_total_${f.periodFrom.slice(0,7)}-${f.periodTo.slice(0,7)}`}
            style={{ marginLeft: "auto" }}
          />
        </div>
        <PlotFigure options={kensuOpts} responsiveWidth />
        <div className="text-xs text-slate-400 mt-1">
          ・緑 実線=実績 ・<span className="text-amber-700">橙 点線=予算</span> (FY2026〜) ・薄灰 線=前年同月 ・y軸=自動調整
        </div>
      </section>

      <div className="print-page-break" aria-hidden />

      {/* 予定 / 緊急 構成 */}
      <section className="card p-4">
        <div className="flex items-center gap-2 mb-2">
          <h2 className="text-base font-semibold">📊 予定 / 緊急 構成 ({scopeLabel}、{avgMode ? "直近12ヶ月移動平均" : "月次"})</h2>
          <span className="text-[10px] text-slate-400">(棒上=月合計、 segment 内=各件数{avgMode ? "・移動平均" : ""})</span>
        </div>
        <PlotFigure options={yoteiOpts} responsiveWidth />
        <div className="text-xs text-slate-400 mt-1">
          予定 + 緊急 = 手術件数（①の手術件数と一致）。 麻酔別(全麻/局麻)は同じ手術件数を分ける別軸。
        </div>
      </section>

      <div className="print-page-break" aria-hidden />

      {/* 診療科別 手術件数 集計表 (病院全体 + 各科 × 月、 対前年%色付け) */}
      <section className="card p-4">
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <h2 className="text-base font-semibold">📋 診療科別 手術件数 集計表</h2>
          <span className="text-xs text-slate-500">病院全体 + 各科 × 月 (対前年%で色付け)</span>
          {avgMode && <span className="perf-pill bg-amber-100 text-amber-800 border-amber-200">各月セル=末尾12ヶ月移動平均</span>}
          <CsvButton
            rows={deptTableCsv as unknown as Record<string, unknown>[]}
            filename={`surgery_dept_table_${f.periodFrom.slice(0,7)}-${f.periodTo.slice(0,7)}`}
            style={{ marginLeft: "auto" }}
          />
        </div>
        <div className="text-[10px] text-slate-500 mb-1 flex flex-wrap items-center gap-x-2 gap-y-1">
          <span className="font-medium">セル色:</span>
          <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 bg-emerald-300 border border-slate-300"></span>+10% 以上</span>
          <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 bg-emerald-100 border border-slate-300"></span>+5% 以上</span>
          <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 bg-white border border-slate-300"></span>±5% 内</span>
          <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 bg-red-100 border border-slate-300"></span>-5% 以下</span>
          <span className="inline-flex items-center gap-1"><span className="inline-block w-3 h-3 bg-red-300 border border-slate-300"></span>-10% 以下</span>
          <span>(対前年同月)</span>
          <span>・対前年 = 当月÷前年 形式 (例: 111.2% → 前年比+11.2%)</span>
        </div>
        {deptTable.rows.length === 0 ? (
          <div className="text-xs text-slate-400 py-8 text-center">データなし</div>
        ) : (
          <div className="overflow-x-auto text-xs">
            <table className="w-full border-collapse">
              <thead className="bg-slate-100 sticky top-0 z-10">
                <tr>
                  <SortTh s={s} sortKey="診療科" get={(r) => (r as DeptTableRow).診療科} className="text-left px-2 py-1 border-b border-slate-200 sticky left-0 bg-slate-100 z-20">診療科</SortTh>
                  {deptTable.months.map((mo) => (
                    <SortTh key={mo} s={s} sortKey={`mo:${mo}`} get={(r) => (r as DeptTableRow).cells.get(mo)?.actual ?? null} className="text-right px-1 py-1 border-b border-slate-200 tabular-nums">{monthLabel(mo)}</SortTh>
                  ))}
                  <SortTh s={s} sortKey="mean" get={(r) => (r as DeptTableRow).actualMean} className="text-right px-2 py-1 border-b border-slate-200 bg-slate-200">月平均</SortTh>
                  <SortTh s={s} sortKey="prevMean" get={(r) => (r as DeptTableRow).prevMean} className="text-right px-2 py-1 border-b border-slate-200 bg-slate-200">月平均(前年)</SortTh>
                  <SortTh s={s} sortKey="ratio" get={(r) => (r as DeptTableRow).ratioMean} className="text-right px-2 py-1 border-b border-slate-200 bg-slate-200">対前年</SortTh>
                </tr>
              </thead>
              <tbody>
                {s.rows.map((r) => {
                  const isTotal = r.診療科 === "病院全体";
                  const isFocused = isTotal ? focusDept == null : focusDept === r.診療科;
                  const totBg = cellBg(r.pctMean);
                  const onClick = () => {
                    if (isTotal) f.setSurgeryFocusedDept(null);
                    else f.setSurgeryFocusedDept(isFocused ? null : r.診療科);
                  };
                  const focusFrame = isFocused;
                  return (
                    <tr key={r.診療科}
                        className={focusFrame
                          ? "border-y-2 border-amber-500"
                          : "border-b border-slate-100 hover:bg-slate-50"}>
                      <td className={`text-left px-2 py-0.5 sticky left-0 z-[1] font-medium bg-white ${focusFrame ? "border-l-2 border-amber-500" : ""}`}>
                        <button
                          onClick={onClick}
                          className={`text-left ${focusFrame ? "font-bold" : "hover:underline decoration-dotted"}`}
                          title={isTotal ? "病院全体。 クリックでフォーカス解除"
                                : isFocused ? "クリックでフォーカス解除"
                                : "この診療科にフォーカス"}
                        >
                          {isTotal ? <span className="mr-1">🏥</span> : isFocused ? <span className="mr-1">🎯</span> : null}
                          {r.診療科}
                        </button>
                      </td>
                      {deptTable.months.map((mo) => {
                        const c = r.cells.get(mo);
                        const v = c?.actual;
                        const p = c?.prev;
                        const pct = (v != null && p != null && p !== 0) ? (v - p) / Math.abs(p) : null;
                        const bg = cellBg(pct);
                        const ratioPct = (v != null && p != null && p !== 0) ? `${(v / p * 100).toFixed(1)}%` : "—";
                        return (
                          <td key={mo} className={`text-right px-1 py-0.5 tabular-nums ${bg}`}
                              title={v != null && p != null
                                ? `${monthLabel(mo)}${avgMode ? " (末尾12ヶ月移動平均)" : ""}\n実績: ${fmtCnt(v)} 件\n前年: ${fmtCnt(p)} 件\n対前年: ${ratioPct}`
                                : v != null ? `実績: ${fmtCnt(v)} 件 (前年データなし)` : "データなし"}>
                            {fmtCnt(v)}
                          </td>
                        );
                      })}
                      <td className="text-right px-2 py-0.5 tabular-nums font-semibold bg-slate-50"
                          title={`月平均: ${r.actualMean != null ? r.actualMean.toFixed(1) : "—"}`}>
                        {r.actualMean != null ? r.actualMean.toFixed(1) : "—"}
                      </td>
                      <td className="text-right px-2 py-0.5 tabular-nums text-slate-500 bg-slate-50"
                          title={`月平均(前年): ${r.prevMean != null ? r.prevMean.toFixed(1) : "—"}`}>
                        {r.prevMean != null ? r.prevMean.toFixed(1) : "—"}
                      </td>
                      <td className={`text-right px-2 py-0.5 tabular-nums font-semibold ${totBg} ${focusFrame ? "border-r-2 border-amber-500" : ""}`}
                          title={`月平均対前年: 当期÷前年 = ${r.ratioMean != null ? `${(r.ratioMean * 100).toFixed(1)}%` : "—"}`}>
                        {r.ratioMean != null ? `${(r.ratioMean * 100).toFixed(1)}%` : "—"}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>
      {/* 手術売上 sections は ユーザ要望で削除済 (2026-05-21、 死蔵コードは 2026-06-28 撤去)。
          診療科定義が入院 (収入分析) と異なり サイドバー frequencies の整合性が取れないため。 */}
    </div>
  );
}
